/**
 * canvas.ts — Canvas 2D renderer lifecycle + frame orchestration.
 *
 * Pure reader of GameState + UiState (never mutates sim state). Any sim
 * mutation flows via Commands (for sim state); UI mutations flow via DOM
 * event handlers that write UiState directly.
 *
 * This module owns the canvas/DPR lifecycle, the viewport rectangle, and the
 * top-level frame composition. The actual drawing of the two views lives in
 * `galaxy-view.ts` and `system-view.ts`.
 *
 * Layout:
 *   - HUD lives at y=0..44 as a fixed DOM overlay (ui/hud.ts).
 *   - Detail panel occupies the right 260px as a fixed DOM overlay
 *     (ui/detail-panel.ts) ONLY when a selection is present.
 *   - The galaxy/system view is the remaining rectangle; that rectangle is
 *     the Viewport passed to the two view renderers.
 */

import type { GameState } from '../state/types/index';
import { transitionProgress, viewerEmpireId, type UiState } from '../ui/state';
import { drawGalaxyView } from './galaxy-view';
import { drawSystemView } from './system-view';
import type { Viewport } from './camera';
import { squareViewport } from './system-view';
import { advanceVfx, createVfxState } from './vfx';
import { drawTiledStarfield, getStarfield } from './backdrop';
import {
  createDiffState,
  getAudioEngine,
  type AudioEngine,
  type AudioFrameContext,
  type DiffState,
} from '../audio';
import { worldToScreen } from './camera';

export interface RenderContext {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  dpr: number;
  /**
   * Render-layer VFX state (v2M7.4a) — active tracers / muzzle flashes /
   * etc., plus the last-tick fire snapshot used by `advanceVfx` for
   * event detection. Session-scoped: created at `initRenderer` and
   * lives until session teardown; reset (along with the rest of
   * RenderContext) on a new session start.
   */
  vfx: import('./vfx').VfxState;
  /**
   * Audio engine (v2M8.1) — module-level singleton, reused across
   * session restarts so we don't leak `AudioContext`s. Held here
   * (rather than re-imported in each draw loop) so `renderFrame`
   * can reach it without tight coupling.
   */
  audio: AudioEngine;
  /**
   * Per-session audio cue detection state — analog of `vfx` but for
   * the audio diff path. Fresh on each `initRenderer` call so
   * lastObservedTick + (future) per-category snapshots reset cleanly
   * when a new game starts.
   */
  audioDiff: DiffState;
}

const HUD_HEIGHT = 44;
const PANEL_WIDTH = 260;
const GALAXY_PADDING = 16;

export function initRenderer(canvas: HTMLCanvasElement): RenderContext {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not acquire 2D rendering context');
  const dpr = window.devicePixelRatio || 1;
  resizeCanvas(canvas, ctx, dpr);
  window.addEventListener('resize', () => resizeCanvas(canvas, ctx, dpr));
  return {
    canvas,
    ctx,
    dpr,
    vfx: createVfxState(),
    audio: getAudioEngine(),
    audioDiff: createDiffState(),
  };
}

function resizeCanvas(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  dpr: number,
): void {
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  canvas.width = Math.floor(w * dpr);
  canvas.height = Math.floor(h * dpr);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}

/** Compute the current viewport (changes when a selection panel is open). */
export function computeViewport(canvas: HTMLCanvasElement, ui: UiState): Viewport {
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  const panelOpen = ui.selection.kind !== 'none';
  const padLeft = GALAXY_PADDING;
  const padTop = HUD_HEIGHT + GALAXY_PADDING;
  const rightReserved = panelOpen ? PANEL_WIDTH : 0;
  const viewW = Math.max(50, w - padLeft - GALAXY_PADDING - rightReserved);
  const viewH = Math.max(50, h - padTop - GALAXY_PADDING);
  return { padLeft, padTop, viewW, viewH };
}

/** `nowMs()` seam — swapped out in tests. */
function nowMs(): number {
  return performance.now();
}

/**
 * v3M19.3 — per-frame render options threaded from the session loop.
 * Currently carries the floating-damage-numbers gameplay toggle; future
 * v3M11 polish-tab settings (VFX quality, particle density, etc.) flow
 * through the same bag so renderFrame's signature stays stable.
 */
export interface RenderFrameOpts {
  showDamageNumbers?: boolean;
}

export function renderFrame(
  rc: RenderContext,
  state: GameState,
  ui: UiState,
  opts: RenderFrameOpts = {},
): void {
  const { ctx, canvas } = rc;
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  const vp = computeViewport(canvas, ui);

  // v2M7.4a: advance VFX state once per frame, before any drawing —
  // detects new fire events (diff against last-tick snapshot) and drops
  // expired tracers. No-op when sim tick hasn't advanced since last call.
  // v3M19.3: pass through the floating-damage-numbers gameplay toggle.
  advanceVfx(rc.vfx, state, { showDamageNumbers: opts.showDamageNumbers });

  // v2M8.1+: dispatch audio cues for any sim transitions observed since
  // the last frame. Mirrors `advanceVfx` — diff-driven, idempotent
  // within a single sim tick. v2M8.4 added weapon cues, which need a
  // camera projection + viewport for positional ops; v2M8.5+ will reuse
  // the same context. The squareVp matches the rectangle ships are
  // rendered against, so audio pan / attenuation align with what the
  // player is looking at.
  const squareVp = squareViewport(vp);
  const audioFrameCtx: AudioFrameContext = {
    viewMode: ui.viewMode,
    focusedSystemId: ui.focusedSystemId,
    viewerEmpireId: viewerEmpireId(state, ui),
    worldToScreen: (sx, sy) => worldToScreen(ui.systemCamera, squareVp, { x: sx, y: sy }),
    viewportCentreX: squareVp.padLeft + squareVp.viewW / 2,
    viewportCentreY: squareVp.padTop + squareVp.viewH / 2,
    viewportWidth: squareVp.viewW,
    viewportHeight: squareVp.viewH,
  };
  rc.audio.onFrame(rc.audioDiff, state, audioFrameCtx);

  // Backdrop.
  ctx.fillStyle = '#020408';
  ctx.fillRect(0, 0, w, h);

  // v2M7.8a: cached tileable starfield with parallax driven by the
  // active view's camera. Galaxy and system view get distinct seeds
  // so their layouts read as different — same `getStarfield` /
  // `drawImage` runtime contract that the v3M11 polish pass will use
  // when commissioned PNGs replace the procedural body. Galaxy view
  // gets a slower parallax (deeper-field feel); system view drifts
  // a touch faster so the player senses motion as they pan a single
  // system's tactical frame.
  const seed = ui.viewMode === 'galaxy' ? 'galaxy-stars' : 'system-stars';
  const camera = ui.viewMode === 'galaxy' ? ui.galaxyCamera : ui.systemCamera;
  const parallaxFactor = ui.viewMode === 'galaxy' ? 200 : 280;
  drawTiledStarfield(
    ctx,
    getStarfield(seed),
    w,
    h,
    camera.centerX * parallaxFactor,
    camera.centerY * parallaxFactor,
  );
  drawViewportFrame(ctx, vp);

  // View composition. The "current" view is ui.viewMode; a transition, if
  // active, fades out the `fromView` and fades in the `toView`. When the
  // transition completes we null it out lazily so we stop paying the
  // double-draw cost.
  const transition = ui.viewTransition;
  const progress = transitionProgress(transition, nowMs());

  if (transition && progress < 1) {
    // Cross-fade: draw out-going view at (1 - progress), then in-coming view
    // at progress.
    ctx.save();
    ctx.globalAlpha = 1 - progress;
    drawViewByMode(transition.fromView, ctx, state, ui, vp, rc.vfx);
    ctx.restore();

    ctx.save();
    ctx.globalAlpha = progress;
    drawViewByMode(transition.toView, ctx, state, ui, vp, rc.vfx);
    ctx.restore();
  } else {
    // No transition (or just finished) — draw the steady-state view.
    if (transition) ui.viewTransition = null;
    drawViewByMode(ui.viewMode, ctx, state, ui, vp, rc.vfx);
  }

  // M8.1-F: when the enemy-POV overlay is on, paint an unmistakable badge
  // so the debugger doesn't confuse the enemy's view for the player's. Kept
  // on the top-level composition layer so it survives both steady-state and
  // mid-transition frames.
  if (ui.enemyPovOverlay) {
    drawEnemyPovBadge(ctx, state, ui, vp);
  }
}

function drawViewByMode(
  mode: UiState['viewMode'],
  ctx: CanvasRenderingContext2D,
  state: GameState,
  ui: UiState,
  vp: Viewport,
  vfx: import('./vfx').VfxState,
): void {
  if (mode === 'galaxy') {
    drawGalaxyView(ctx, state, ui, vp);
  } else {
    drawSystemView(ctx, state, ui, vp, vfx);
  }
}

function drawViewportFrame(ctx: CanvasRenderingContext2D, vp: Viewport): void {
  ctx.save();
  ctx.strokeStyle = 'rgba(80,110,160,0.25)';
  ctx.lineWidth = 1;
  ctx.strokeRect(vp.padLeft, vp.padTop, vp.viewW, vp.viewH);
  ctx.restore();
}

/**
 * Dev-only top-right badge that reads e.g. "ENEMY POV: Krell Dominion" when
 * F10 is toggled (M8.1-F). Painted on the top composition layer so the test
 * can't mistake the swapped fog for a sim bug. Shows the empire's authored
 * name + colour so single-empire / multi-empire scenarios both self-explain.
 */
function drawEnemyPovBadge(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  ui: UiState,
  vp: Viewport,
): void {
  const viewerId = viewerEmpireId(state, ui);
  const empire = state.empires.get(viewerId);
  const label = empire ? `ENEMY POV: ${empire.name}` : 'ENEMY POV';
  const color = empire?.color ?? '#ff8f8f';
  ctx.save();
  ctx.font = '600 11px system-ui, sans-serif';
  ctx.textBaseline = 'top';
  ctx.textAlign = 'right';
  const padX = 8;
  const padY = 5;
  const metrics = ctx.measureText(label);
  const w = metrics.width + padX * 2;
  const h = 22;
  const x = vp.padLeft + vp.viewW - w - 4;
  const y = vp.padTop + 4;
  ctx.fillStyle = 'rgba(20, 8, 12, 0.8)';
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.fillText(label, x + w - padX, y + padY);
  ctx.restore();
}
